import React, { useEffect, useState } from "react";
import { CheckCircleIcon } from "asset/icons";
import OnboardingLayout from "component/auth/OnboardingLayout";
import Loader from "component/loader";
import FailHandler from "component/failhandler";

import { fetchCurrentBusiness } from "api/fetchers";
import {
    handleFetchBusinessById,
    handleUpdateBusinessDocuments,
    handleUploadOnboardingDocument,
} from "api/onboarding";
import { Helmet } from "react-helmet";
import { useLocation, useNavigate } from "react-router-dom";
import UploadInputField from "component/auth/onboarding/UploadInputField";
import SampleDocumentModal from "component/auth/onboarding/SampleDocumentModal";
import { getComplianceSampleDocuments } from "component/auth/onboarding/onboardingSampleDocuments";

const steps = [
    { title: "Business information" },
    { title: "Business verification", subtitles: ["Compliance", "Wallets"] },
    { title: "Business ownership" },
];

const SoleProprietorshipDocuments = ({ currentStep = 1, onNext, onBack }) => {
    const [currentBusiness, setCurrentBusiness] = useState(null);
    const [fails, setFails] = useState("");
    const [errorMessage, setErrorMessage] = useState("");
    const navigate = useNavigate();
    const location = useLocation();
    const shouldReturnToSummary =
        new URLSearchParams(location.search).get("returnTo") === "summary";

    const [selectedCACOption, setSelectedCACOption] = useState("CAC_STATUS_REPORT");
    const [showModal, setShowModal] = useState(false);
    const [selectedSample, setSelectedSample] = useState(null);
    const [loader, setLoader] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState({});
    const [uploadProgress, setUploadProgress] = useState({});
    const [bankStatementPrepopulated, setBankStatementPrepopulated] = useState(false);
    const [showRequiredErrors, setShowRequiredErrors] = useState(false);

    const documentKeyMap = {
        TRADE_BUSINESS_LICENSE: "BUSINESS_LICENSE",
        BANK_STATEMENT: "BankStatement",
    };

    const business = currentBusiness?.business || currentBusiness;
    const businessCountryName = business?.country || business?.businessCountry?.name;
    const businessCountryCode = business?.businessCountry?.code;
    const businessContinent = business?.businessCountry?.continent;
    const middleEastContinents = ["ME", "MENA", "Middle East", "Middle-East"];
    const middleEastCountryCodes = [
        "AE",
        "BH",
        "CY",
        "EG",
        "IL",
        "IQ",
        "IR",
        "JO",
        "KW",
        "LB",
        "OM",
        "PS",
        "QA",
        "SA",
        "SY",
        "TR",
        "YE",
    ];
    const middleEastCountryNames = [
        "United Arab Emirates",
        "Bahrain",
        "Cyprus",
        "Egypt",
        "Israel",
        "Iraq",
        "Iran",
        "Jordan",
        "Kuwait",
        "Lebanon",
        "Oman",
        "Palestine",
        "Qatar",
        "Saudi Arabia",
        "Syria",
        "Turkey",
        "Yemen",
    ];
    const isNigeria = businessCountryName === "Nigeria" || businessCountryCode === "NG";
    const isMiddleEast =
        middleEastContinents.includes(businessContinent) ||
        middleEastCountryCodes.includes(businessCountryCode) ||
        middleEastCountryNames.includes(businessCountryName);
    const sampleDocuments = getComplianceSampleDocuments(
        businessCountryName || businessCountryCode
    );
    // File upload state management
    const [fileUploaded, setFileUploaded] = useState({
        CAC: false,
        COMMERCIAL_REGISTRATION_CERTIFICATE: false,
        TRADE_BUSINESS_LICENSE: false,
        APPLICATION_LETTER: false,
        BANK_STATEMENT: false,
        CAC_STATUS_REPORT: false,
        CAC_FORM_1: false,
    });
    const [loading, setLoading] = useState({
        CAC: false,
        COMMERCIAL_REGISTRATION_CERTIFICATE: false,
        TRADE_BUSINESS_LICENSE: false,
        APPLICATION_LETTER: false,
        BANK_STATEMENT: false,
        CAC_STATUS_REPORT: false,
        CAC_FORM_1: false,
    });

    // Fetch current business on load
    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await fetchCurrentBusiness();
                const currentBusinessData = data?.business || data;
                let businessData = currentBusinessData;

                if (currentBusinessData?._id) {
                    const businessDetails = await handleFetchBusinessById(currentBusinessData._id);
                    businessData = businessDetails?.business || businessDetails || currentBusinessData;
                }

                setCurrentBusiness(businessData);

                const existingDocuments = businessData?.documents || {};
                const apiToFormKey = {
                    BankStatement: "BANK_STATEMENT",
                    BANK_STATEMENT: "BANK_STATEMENT",
                    BUSINESS_LICENSE: "TRADE_BUSINESS_LICENSE",
                };
                const hydratedFiles = {};
                const hydratedFlags = {};
                Object.entries(existingDocuments).forEach(([apiKey, document]) => {
                    const formKey = apiToFormKey[apiKey] || apiKey;
                    const url = getUploadUrl(document);
                    const key = getUploadKey(document, apiKey);
                    if (!url && !key) return;
                    hydratedFiles[formKey] = {
                        ...((document && typeof document === "object") ? document : {}),
                        name: document?.name || document?.key || apiKey,
                        size: document?.size || 0,
                        ...(url ? { url } : {}),
                        ...(key ? { key } : {}),
                    };
                    hydratedFlags[formKey] = true;
                });
                setUploadedFiles((prev) => ({ ...prev, ...hydratedFiles }));
                setFileUploaded((prev) => ({ ...prev, ...hydratedFlags }));
                if (hydratedFlags.CAC_FORM_1) setSelectedCACOption("BN_FORMS");
                else if (hydratedFlags.CAC_STATUS_REPORT) setSelectedCACOption("CAC_STATUS_REPORT");

                const bankStatementProof = getBankStatementProofFile(businessData);
                if (bankStatementProof) {
                    setBankStatementPrepopulated(true);
                    setFileUploaded((prev) => ({ ...prev, BANK_STATEMENT: true }));
                    setUploadedFiles((prev) => ({
                        ...prev,
                        BANK_STATEMENT: bankStatementProof,
                    }));
                }
            } catch (error) {
                console.error("Failed to fetch current business:", error);
            }
        };
        fetchData();
    }, []);

    const getUploadUrl = (raw) =>
        (typeof raw === "string" && raw) ||
        raw?.url ||
        raw?.url?.link ||
        raw?.link ||
        raw?.data?.url ||
        raw?.data?.url?.link ||
        raw?.data?.link ||
        "";

    const getUploadKey = (raw, fallback = "") =>
        raw?.key ||
        raw?.fileKey ||
        raw?.name ||
        raw?.Key ||
        raw?.data?.key ||
        raw?.data?.fileKey ||
        raw?.data?.name ||
        raw?.data?.Key ||
        raw?.path ||
        raw?.data?.path ||
        fallback;

    const getApiDocumentKey = (fileKey) => documentKeyMap[fileKey] || fileKey;

    // Handle file upload with validation
    const handleFileUpload = async (event, fileKey) => {
        const file = event.target.files[0];
        if (!file || file.size > 5 * 1024 * 1024) {
            setFails(true);
            setErrorMessage("File size exceeds 5MB limit.");
            return;
        }

        setLoading((prev) => ({ ...prev, [fileKey]: true }));
        setUploadedFiles((prev) => ({ ...prev, [fileKey]: file }));

        // Simulate progress
        let progress = 0;
        const interval = setInterval(() => {
            progress += 10;
            setUploadProgress((prev) => ({ ...prev, [fileKey]: progress }));
            if (progress >= 100) clearInterval(interval);
        }, 100);

        try {
            const response = await handleUploadOnboardingDocument(file);
            const uploadData = response?.data || response;
            const url = getUploadUrl(uploadData);

            if (!url) {
                throw new Error(response?.message || "No upload URL returned");
            }

            setUploadedFiles((prev) => ({
                ...prev,
                [fileKey]: {
                    ...file,
                    name: file.name,
                    size: file.size,
                    url,
                    key: getUploadKey(uploadData, file.name),
                },
            }));
            setFileUploaded((prev) => ({ ...prev, [fileKey]: true }));
        } catch (error) {
            setFails(true);
            setErrorMessage(error?.message || "Upload failed.");
        } finally {
            setLoading((prev) => ({ ...prev, [fileKey]: false }));
        }
    };

    const handleRemoveFile = (fileKey) => {
        if (fileKey === "BANK_STATEMENT" && bankStatementPrepopulated) return;

        setUploadedFiles((prev) => ({ ...prev, [fileKey]: null }));
        setFileUploaded((prev) => ({ ...prev, [fileKey]: false }));
        setUploadProgress((prev) => ({ ...prev, [fileKey]: 0 }));
    };

    function getBankStatementProofFile(businessData) {
        const proof = businessData?.proofOfAddress;
        const isBankStatement = [proof?.type, proof?.key, proof?.name, proof?.url, proof?.link].some(
            (value) => typeof value === "string" && value.toLowerCase().includes("bank")
        );

        const existingBankStatement =
            businessData?.documents?.BankStatement ||
            businessData?.documents?.BANK_STATEMENT ||
            businessData?.documents?.bankStatement;

        if ((!proof || !isBankStatement) && !existingBankStatement) return null;

        const source = isBankStatement ? proof : existingBankStatement;
        const sourceFields = typeof source === "object" && source !== null ? source : {};

        const url = getUploadUrl(source);
        const key = getUploadKey(source, source.name || source.type || "Bank statement");

        return {
            ...sourceFields,
            name: source.name || source.key || source.type || "Bank statement",
            size: source.size || 0,
            ...(url ? { url } : {}),
            ...(key ? { key } : {}),
        };
    }

    // All documents uploaded check
    const allFilesUploaded = () => {
        if (!business) return false;

        const hasApplicationAndBankStatement =
            fileUploaded.APPLICATION_LETTER && fileUploaded.BANK_STATEMENT;

        if (isMiddleEast) {
            return (
                fileUploaded.COMMERCIAL_REGISTRATION_CERTIFICATE &&
                fileUploaded.TRADE_BUSINESS_LICENSE &&
                hasApplicationAndBankStatement
            );
        }

        if (isNigeria) {
            return (
                fileUploaded.CAC &&
                hasApplicationAndBankStatement &&
                (fileUploaded.CAC_STATUS_REPORT || fileUploaded.CAC_FORM_1)
            );
        }

        return fileUploaded.TRADE_BUSINESS_LICENSE && hasApplicationAndBankStatement;
    };

    const getRequiredDocumentKeys = () => {
        const hasApplicationAndBankStatement = ["APPLICATION_LETTER", "BANK_STATEMENT"];

        if (isMiddleEast) {
            return [
                "COMMERCIAL_REGISTRATION_CERTIFICATE",
                "TRADE_BUSINESS_LICENSE",
                ...hasApplicationAndBankStatement,
            ];
        }

        if (isNigeria) {
            return [
                "CAC",
                ...hasApplicationAndBankStatement,
                selectedCACOption === "BN_FORMS" ? "CAC_FORM_1" : "CAC_STATUS_REPORT",
            ];
        }

        return ["TRADE_BUSINESS_LICENSE", ...hasApplicationAndBankStatement];
    };

    const buildDocumentsPayload = () => {
        const documents = {};

        getRequiredDocumentKeys().forEach((fileKey) => {
            if (fileKey === "BANK_STATEMENT" && bankStatementPrepopulated) return;

            const file = uploadedFiles[fileKey];
            const url = getUploadUrl(file);
            const key = getUploadKey(file, file?.name || "");

            if (!url && !key) return;

            documents[getApiDocumentKey(fileKey)] = {
                ...(url ? { url } : {}),
                ...(key ? { key } : {}),
            };
        });

        return { documents };
    };

    const isRequiredDocumentMissing = (fileKey) =>
        getRequiredDocumentKeys().includes(fileKey) &&
        !(fileUploaded[fileKey] ||
            (fileKey === "BANK_STATEMENT" && bankStatementPrepopulated));

    // Handle continue button
    const handleContinue = async () => {
        if (allFilesUploaded()) {
            setLoader(true);
            try {
                await handleUpdateBusinessDocuments(business?._id, buildDocumentsPayload());
                navigate(
                    shouldReturnToSummary
                        ? "/onboarding/summary"
                        : "/onboarding/business-verification/wallets"
                );
            } catch (error) {
                setFails(true);
                setErrorMessage(error?.message || "An error occurred during verification.");
            } finally {
                setLoader(false);
            }
        } else {
            setShowRequiredErrors(true);
            setFails(true);
            setErrorMessage("Ensure all required documents are uploaded before proceeding.");
            requestAnimationFrame(() => {
                const firstMissingKey = getRequiredDocumentKeys().find(isRequiredDocumentMissing);
                const field = document.querySelector(
                    `[data-onboarding-field="${firstMissingKey}"]`
                );
                field?.scrollIntoView({ behavior: "smooth", block: "center" });
                field?.querySelector("input")?.focus({ preventScroll: true });
            });
        }
    };

    // Open modal for document samples
    const openSampleModal = (documentKey) => {
        const documentName =
            documentKey === "CAC_STATUS_REPORT"
                ? "CAC Status Report"
                : documentKey === "BN_FORMS"
                  ? "CAC BN Form 1"
                  : documentKey;

        setSelectedSample(documentName);
        setShowModal(true);
    };

    const renderUploadField = ({ key, label, subtitle, sample }) => (
        <div key={key} data-onboarding-field={key}>
            <UploadInputField
                label={label}
                subtitle={subtitle}
                fileKey={key}
                seeSampleDoc={sample || sampleDocuments[label]}
                fileState={{
                    file: uploadedFiles[key],
                    loading: loading[key],
                    uploaded: fileUploaded[key],
                    progress: uploadProgress[key] || 0,
                }}
                onFileChange={(e) => handleFileUpload(e, key)}
                onRemove={() => handleRemoveFile(key)}
                disabled={key === "BANK_STATEMENT" && bankStatementPrepopulated}
                validationError={
                    showRequiredErrors && isRequiredDocumentMissing(key)
                        ? "This field is required."
                        : ""
                }
            />
        </div>
    );

    const soleProprietorshipDocumentFields = (() => {
        if (isMiddleEast) {
            return [
                {
                    key: "COMMERCIAL_REGISTRATION_CERTIFICATE",
                    label: "Commercial Registration Certificate",
                },
                {
                    key: "TRADE_BUSINESS_LICENSE",
                    label: "Trade/Business License",
                },
                {
                    key: "APPLICATION_LETTER",
                    label: "Application letter to open an account with Tradevu",
                    sample: sampleDocuments["Application letter to open an account with TradeVu"],
                },
                {
                    key: "BANK_STATEMENT",
                    label: "Bank statement or letter (last 6 months)",
                },
            ];
        }

        if (isNigeria) {
            return [
                {
                    key: "CAC",
                    label: "Certificate of Incorporation or Business License",
                    subtitle: "From your country",
                },
                {
                    key: "APPLICATION_LETTER",
                    label: "Application letter to open an account with TradeVu",
                },
                {
                    key: "BANK_STATEMENT",
                    label: "Bank statement (last 6 months)",
                },
            ];
        }

        return [
            {
                key: "TRADE_BUSINESS_LICENSE",
                label: "Certificate of Incorporation or Business License",
            },
            {
                key: "APPLICATION_LETTER",
                label: "Application letter to open an account with Tradevu",
                sample: sampleDocuments["Application letter to open an account with TradeVu"],
            },
            {
                key: "BANK_STATEMENT",
                label: "Bank statement (last 6 months)",
            },
        ];
    })();

    return (
        <OnboardingLayout
            title="Activate account"
            steps={steps}
            currentStep={currentStep}
            currentSubStep={0}
        >
            <div className="max-w-[1100px] md:pl-96  ">
                <Helmet>
                    <title>Upload Business Documents</title>
                </Helmet>

                <div className="">
                    <FailHandler success={fails} message={errorMessage} />

                    <h2 className="aku-heading-h2-medium text-[#101828]">
                        Verify business
                    </h2>

                    <div>
                        <div className="mt-8">
                            <div className="space-y-4">
                                {soleProprietorshipDocumentFields.map(renderUploadField)}
                            </div>
                        </div>

                        {isNigeria && (
                            <>
                                <div className="mt-6">
                                    <p className="aku-body-small-medium !mb-1 text-[#4F5372]">
                                        Upload either your <span className="">Status Report</span>{" "}
                                        or <span className="">BN Form 1</span>
                                    </p>
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-6">
                                            {/* Status Report Option */}
                                            <div className="flex items-center">
                                                <input
                                                    type="radio"
                                                    id="status-report-radio"
                                                    name="cacOption"
                                                    value="CAC_STATUS_REPORT"
                                                    className="hidden"
                                                    checked={
                                                        selectedCACOption === "CAC_STATUS_REPORT"
                                                    }
                                                    onChange={() =>
                                                        setSelectedCACOption("CAC_STATUS_REPORT")
                                                    }
                                                />
                                                <label
                                                    htmlFor="status-report-radio"
                                                    className="flex items-center justify-center cursor-pointer"
                                                >
                                                    {selectedCACOption === "CAC_STATUS_REPORT" ? (
                                                        <CheckCircleIcon className="size-5" />
                                                    ) : (
                                                        <CheckCircleIcon className="size-5 *:fill-[#DBDFE5]" />
                                                    )}
                                                </label>
                                                <span
                                                    className="aku-body-small-medium ml-2 text-gray-700 cursor-pointer"
                                                    onClick={() =>
                                                        setSelectedCACOption("CAC_STATUS_REPORT")
                                                    }
                                                >
                                                    CAC Status Report
                                                </span>
                                            </div>

                                            {/* BN Form Option */}
                                            <div className="flex items-center">
                                                <input
                                                    type="radio"
                                                    id="bn-forms-radio"
                                                    name="cacOption"
                                                    value="BN_FORMS"
                                                    className="hidden"
                                                    checked={selectedCACOption === "BN_FORMS"}
                                                    onChange={() =>
                                                        setSelectedCACOption("BN_FORMS")
                                                    }
                                                />
                                                <label
                                                    htmlFor="bn-forms-radio"
                                                    className="flex items-center justify-center cursor-pointer"
                                                >
                                                    {selectedCACOption === "BN_FORMS" ? (
                                                        <CheckCircleIcon className="size-5" />
                                                    ) : (
                                                        <CheckCircleIcon className="size-5 *:fill-[#DBDFE5]" />
                                                    )}
                                                </label>
                                                <span
                                                    className="aku-body-small-medium ml-2 text-gray-700 cursor-pointer"
                                                    onClick={() => setSelectedCACOption("BN_FORMS")}
                                                >
                                                    CAC BN Form 1
                                                </span>
                                            </div>
                                        </div>

                                        {/* See Sample Button */}
                                        <span
                                            className="text-[#6F00FF] text-base font-normal flex gap-1 items-center cursor-pointer"
                                            onClick={() => openSampleModal(selectedCACOption)}
                                        >
                                            See sample{" "}
                                            <span className="material-icons">chevron_right</span>
                                        </span>
                                    </div>

                                    {selectedCACOption === "CAC_STATUS_REPORT" && (
                                        <div data-onboarding-field="CAC_STATUS_REPORT">
                                        <UploadInputField
                                            subtitle="JPEG, PNG, PDF ・ 5 MB max."
                                            fileKey="cacFile"
                                            fileState={{
                                                file: uploadedFiles["CAC_STATUS_REPORT"],
                                                loading: loading["CAC_STATUS_REPORT"],
                                                uploaded: fileUploaded["CAC_STATUS_REPORT"],
                                                progress: uploadProgress["CAC_STATUS_REPORT"] || 0,
                                            }}
                                            onFileChange={(e) =>
                                                handleFileUpload(e, "CAC_STATUS_REPORT")
                                            }
                                            onRemove={() => handleRemoveFile("CAC_STATUS_REPORT")}
                                            validationError={
                                                showRequiredErrors &&
                                                isRequiredDocumentMissing("CAC_STATUS_REPORT")
                                                    ? "This field is required."
                                                    : ""
                                            }
                                        />
                                        </div>
                                    )}

                                    {selectedCACOption === "BN_FORMS" && (
                                        <div data-onboarding-field="CAC_FORM_1">
                                        <UploadInputField
                                            subtitle="JPEG, PNG, PDF ・ 5 MB max."
                                            fileKey="bnFormsFile"
                                            fileState={{
                                                file: uploadedFiles["CAC_FORM_1"],
                                                loading: loading["CAC_FORM_1"],
                                                uploaded: fileUploaded["CAC_FORM_1"],
                                                progress: uploadProgress["CAC_FORM_1"] || 0,
                                            }}
                                            onFileChange={(e) => handleFileUpload(e, "CAC_FORM_1")}
                                            onRemove={() => handleRemoveFile("CAC_FORM_1")}
                                            validationError={
                                                showRequiredErrors &&
                                                isRequiredDocumentMissing("CAC_FORM_1")
                                                    ? "This field is required."
                                                    : ""
                                            }
                                        />
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>
                    <div className="flex justify-end">
                        <button
                            className={`mt-4 text-base font-normal rounded-lg w-full md:w-auto h-12 px-6 flex items-center justify-center
        ${
            allFilesUploaded()
                ? "bg-[#6F00FF] text-white"
                : "bg-[#E9D7FE] text-white cursor-not-allowed"
        }`}
                            onClick={handleContinue}
                            disabled={loader}
                            aria-disabled={!allFilesUploaded() || loader}
                        >
                            {loader ? <Loader /> : "Save and Continue"}
                        </button>
                    </div>
                </div>
                <SampleDocumentModal
                    isOpen={showModal}
                    onClose={() => setShowModal(false)}
                    title={selectedSample || "Sample Document"}
                    tabs={sampleDocuments[selectedCACOption] ? [selectedCACOption] : []}
                    onTabChange={setSelectedSample}
                    documentSrc={selectedSample ? sampleDocuments[selectedSample] : ""}
                />
            </div>
        </OnboardingLayout>
    );
};

export default SoleProprietorshipDocuments;
